'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

// ─── Types ───────────────────────────────────────────────────────────────────
interface ProductOverview {
  total_products: number;
  active_products: number;
  pending_approval: number;
  draft_products: number;
  suppliers_connected: number;
  categories: number;
  destinations: number;
  avg_quality_score: number;
  ai_enriched: number;
  last_sync: string;
}

interface Product {
  id: string;
  name: string;
  category: string;
  supplier: string;
  destination: string;
  price: number;
  currency: string;
  status: string;
  quality_score: number;
  languages: number;
  images: number;
  last_updated: string;
  is_featured: boolean;
  ai_enriched: boolean;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  product_count: number;
  active: number;
  suppliers: number;
  avg_price: number;
  color: string;
}

interface Supplier {
  id: string;
  name: string;
  type: string;
  products_mapped: number;
  sync_status: string;
  last_sync: string;
  quality_avg: number;
  coverage: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n: number) => n?.toLocaleString() ?? '0';
const fmtCurrency = (n: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n || 0);

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
  pending: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
  draft: 'bg-slate-500/20 text-slate-300 border border-slate-500/30',
  rejected: 'bg-red-500/20 text-red-300 border border-red-500/30',
  archived: 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30',
  live: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
  syncing: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
  error: 'bg-red-500/20 text-red-300 border border-red-500/30',
};

const CATEGORY_DATA: Category[] = [
  { id: 'flights', name: 'Flights', icon: 'PaperAirplaneIcon', product_count: 18420, active: 17890, suppliers: 3, avg_price: 1240, color: 'from-sky-600/30 to-sky-800/10' },
  { id: 'hotels', name: 'Hotels', icon: 'BuildingOffice2Icon', product_count: 9870, active: 9340, suppliers: 4, avg_price: 380, color: 'from-amber-600/30 to-amber-800/10' },
  { id: 'tours', name: 'Tours', icon: 'MapIcon', product_count: 6540, active: 6120, suppliers: 5, avg_price: 2800, color: 'from-emerald-600/30 to-emerald-800/10' },
  { id: 'activities', name: 'Activities', icon: 'StarIcon', product_count: 12300, active: 11800, suppliers: 2, avg_price: 145, color: 'from-purple-600/30 to-purple-800/10' },
  { id: 'cruises', name: 'Cruises', icon: 'GlobeAltIcon', product_count: 1240, active: 1180, suppliers: 3, avg_price: 8900, color: 'from-blue-600/30 to-blue-800/10' },
  { id: 'transfers', name: 'Transfers', icon: 'TruckIcon', product_count: 4200, active: 4050, suppliers: 2, avg_price: 95, color: 'from-orange-600/30 to-orange-800/10' },
  { id: 'buses', name: 'Buses', icon: 'TruckIcon', product_count: 890, active: 840, suppliers: 2, avg_price: 65, color: 'from-teal-600/30 to-teal-800/10' },
  { id: 'insurance', name: 'Insurance', icon: 'ShieldCheckIcon', product_count: 320, active: 310, suppliers: 2, avg_price: 180, color: 'from-rose-600/30 to-rose-800/10' },
  { id: 'private_aviation', name: 'Private Aviation', icon: 'PaperAirplaneIcon', product_count: 180, active: 175, suppliers: 1, avg_price: 24000, color: 'from-zinc-600/30 to-zinc-800/10' },
];

const SUPPLIER_DATA: Supplier[] = [
  { id: 'gadventures', name: 'G Adventures', type: 'Tours', products_mapped: 6540, sync_status: 'live', last_sync: '2 min ago', quality_avg: 94, coverage: '100%' },
  { id: 'viator', name: 'Viator', type: 'Activities', products_mapped: 12300, sync_status: 'live', last_sync: '5 min ago', quality_avg: 91, coverage: '100%' },
  { id: 'bokun', name: 'Bokun', type: 'Tours & Activities', products_mapped: 3200, sync_status: 'syncing', last_sync: '12 min ago', quality_avg: 88, coverage: '87%' },
  { id: 'ttc', name: 'TTC Group', type: 'Tours', products_mapped: 1840, sync_status: 'live', last_sync: '8 min ago', quality_avg: 96, coverage: '100%' },
  { id: 'tourradar', name: 'TourRadar', type: 'Tours', products_mapped: 2100, sync_status: 'live', last_sync: '15 min ago', quality_avg: 89, coverage: '92%' },
  { id: 'crystal', name: 'Crystal Cruises', type: 'Cruises', products_mapped: 1240, sync_status: 'live', last_sync: '1 hr ago', quality_avg: 98, coverage: '100%' },
  { id: 'hotels_api', name: 'Hotels API', type: 'Hotels', products_mapped: 9870, sync_status: 'live', last_sync: '3 min ago', quality_avg: 87, coverage: '95%' },
  { id: 'flights_api', name: 'Flights GDS', type: 'Flights', products_mapped: 18420, sync_status: 'live', last_sync: '1 min ago', quality_avg: 92, coverage: '100%' },
  { id: 'insurance_api', name: 'Insurance Partners', type: 'Insurance', products_mapped: 320, sync_status: 'live', last_sync: '30 min ago', quality_avg: 95, coverage: '100%' },
];

const PRODUCT_SAMPLE: Product[] = [
  { id: 'p001', name: 'Machu Picchu Classic Trek — 8 Days', category: 'Tours', supplier: 'G Adventures', destination: 'Peru', price: 2890, currency: 'USD', status: 'active', quality_score: 97, languages: 8, images: 24, last_updated: '2 hrs ago', is_featured: true, ai_enriched: true },
  { id: 'p002', name: 'Santorini Sunset Sailing Experience', category: 'Activities', supplier: 'Viator', destination: 'Greece', price: 145, currency: 'USD', status: 'active', quality_score: 94, languages: 6, images: 18, last_updated: '5 hrs ago', is_featured: true, ai_enriched: true },
  { id: 'p003', name: 'Crystal Symphony — Mediterranean Voyage', category: 'Cruises', supplier: 'Crystal Cruises', destination: 'Mediterranean', price: 12400, currency: 'USD', status: 'active', quality_score: 99, languages: 12, images: 48, last_updated: '1 day ago', is_featured: true, ai_enriched: true },
  { id: 'p004', name: 'Tokyo Cultural Immersion — 5 Days', category: 'Tours', supplier: 'TTC Group', destination: 'Japan', price: 3200, currency: 'USD', status: 'pending', quality_score: 82, languages: 4, images: 12, last_updated: '3 hrs ago', is_featured: false, ai_enriched: false },
  { id: 'p005', name: 'Serengeti Safari — 7 Nights', category: 'Tours', supplier: 'G Adventures', destination: 'Tanzania', price: 5800, currency: 'USD', status: 'active', quality_score: 96, languages: 7, images: 36, last_updated: '6 hrs ago', is_featured: true, ai_enriched: true },
  { id: 'p006', name: 'Dubai Private Jet Charter', category: 'Private Aviation', supplier: 'Private Aviation', destination: 'UAE', price: 28000, currency: 'USD', status: 'active', quality_score: 98, languages: 5, images: 22, last_updated: '2 days ago', is_featured: false, ai_enriched: true },
  { id: 'p007', name: 'Bali Wellness Retreat — 10 Days', category: 'Tours', supplier: 'TourRadar', destination: 'Indonesia', price: 3600, currency: 'USD', status: 'draft', quality_score: 71, languages: 3, images: 8, last_updated: '1 hr ago', is_featured: false, ai_enriched: false },
  { id: 'p008', name: 'Paris Airport Transfer — CDG to City', category: 'Transfers', supplier: 'Transfers API', destination: 'France', price: 85, currency: 'USD', status: 'active', quality_score: 88, languages: 6, images: 4, last_updated: '30 min ago', is_featured: false, ai_enriched: true },
];

const APPROVAL_QUEUE = [
  { id: 'aq001', product: 'Tokyo Cultural Immersion — 5 Days', supplier: 'TTC Group', category: 'Tours', submitted: '3 hrs ago', quality: 82, reviewer: 'Auto-AI', issues: ['Missing hero image', 'SEO description too short'] },
  { id: 'aq002', product: 'Bali Wellness Retreat — 10 Days', supplier: 'TourRadar', category: 'Tours', submitted: '1 hr ago', quality: 71, reviewer: 'Pending', issues: ['Low quality score', 'No multi-language content', 'Missing pricing for 3 currencies'] },
  { id: 'aq003', product: 'Iceland Northern Lights — 4 Days', supplier: 'G Adventures', category: 'Tours', submitted: '45 min ago', quality: 91, reviewer: 'Auto-AI', issues: ['Minor: Add video content'] },
  { id: 'aq004', product: 'Maldives Overwater Villa Package', supplier: 'Hotels API', category: 'Hotels', submitted: '2 hrs ago', quality: 88, reviewer: 'Pending', issues: ['Duplicate detected — similar to p0234', 'Price mismatch across currencies'] },
];

const COLLECTIONS = [
  { id: 'c001', name: 'Honeymoon Escapes', products: 284, views: 12400, conversions: 8.4, status: 'active', ai_curated: true },
  { id: 'c002', name: 'Adventure Seekers', products: 512, views: 28900, conversions: 11.2, status: 'active', ai_curated: true },
  { id: 'c003', name: 'Luxury Voyages', products: 148, views: 9800, conversions: 14.7, status: 'active', ai_curated: false },
  { id: 'c004', name: 'Family Holidays', products: 390, views: 18200, conversions: 9.1, status: 'active', ai_curated: true },
  { id: 'c005', name: 'Cultural Heritage', products: 220, views: 7400, conversions: 7.8, status: 'active', ai_curated: false },
  { id: 'c006', name: 'Wellness & Spa', products: 165, views: 6100, conversions: 10.3, status: 'draft', ai_curated: true },
];

const TABS = [
  { id: 'catalog', label: 'Product Catalog', icon: 'CubeIcon' },
  { id: 'categories', label: 'Categories & Destinations', icon: 'TagIcon' },
  { id: 'suppliers', label: 'Supplier Mapping', icon: 'BuildingStorefrontIcon' },
  { id: 'approval', label: 'Approval Workflow', icon: 'ClipboardDocumentCheckIcon' },
  { id: 'collections', label: 'Collections & AI', icon: 'SparklesIcon' },
  { id: 'analytics', label: 'Performance Analytics', icon: 'ChartBarIcon' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
function KPICard({ label, value, sub, icon, color }: { label: string; value: string; sub?: string; icon: string; color: string }) {
  return (
    <div className={`card p-4 border border-border/50 bg-gradient-to-br ${color}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-white/5`}>
          <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-primary" />
        </div>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      {sub && <p className="text-2xs text-primary mt-1">{sub}</p>}
    </div>
  );
}

function QualityBar({ score }: { score: number }) {
  const color = score >= 90 ? 'bg-emerald-500' : score >= 75 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-medium text-foreground w-8 text-right">{score}</span>
    </div>
  );
}

// ─── Tab: Product Catalog ─────────────────────────────────────────────────────
function CatalogTab({ products }: { products: Product[] }) {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.supplier.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || p.category === filterCat;
    const matchStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  const categories = Array.from(new Set(products.map(p => p.category)));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search products, suppliers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field pl-9 py-2 text-sm w-full"
          />
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="input-field py-2 text-sm">
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input-field py-2 text-sm">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="draft">Draft</option>
        </select>
        <button className="btn-primary text-sm px-4 py-2 flex items-center gap-2">
          <Icon name="ArrowUpTrayIcon" size={14} />
          Bulk Import
        </button>
        <button className="btn-secondary text-sm px-4 py-2 flex items-center gap-2">
          <Icon name="ArrowDownTrayIcon" size={14} />
          Export
        </button>
      </div>

      {/* Table */}
      <div className="card border border-border/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-white/2">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Product</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Supplier</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Price</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Quality</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Flags</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-foreground text-sm leading-tight">{p.name}</p>
                      <p className="text-2xs text-muted-foreground mt-0.5">{p.destination} · {p.images} images · {p.languages} langs</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{p.category}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{p.supplier}</td>
                  <td className="px-4 py-3 text-foreground text-sm font-medium">{fmtCurrency(p.price, p.currency)}</td>
                  <td className="px-4 py-3 w-28"><QualityBar score={p.quality_score} /></td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold ${STATUS_COLORS[p.status] ?? ''}`}>
                      {p.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {p.is_featured && <span className="px-1.5 py-0.5 rounded text-2xs bg-amber-500/20 text-amber-300">★ Featured</span>}
                      {p.ai_enriched && <span className="px-1.5 py-0.5 rounded text-2xs bg-purple-500/20 text-purple-300">AI</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button className="p-1.5 rounded hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors" title="Edit">
                        <Icon name="PencilSquareIcon" size={14} />
                      </button>
                      <button className="p-1.5 rounded hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors" title="AI Enrich">
                        <Icon name="SparklesIcon" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border/30 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Showing {filtered.length} of {products.length} products</p>
          <div className="flex gap-2">
            <button className="btn-secondary text-xs px-3 py-1.5">Previous</button>
            <button className="btn-secondary text-xs px-3 py-1.5">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Categories & Destinations ──────────────────────────────────────────
function CategoriesTab() {
  const destinations = [
    { name: 'Europe', products: 18400, top: 'France, Italy, Greece', growth: '+12%' },
    { name: 'Asia Pacific', products: 14200, top: 'Japan, Bali, Thailand', growth: '+18%' },
    { name: 'Americas', products: 11800, top: 'Peru, USA, Mexico', growth: '+9%' },
    { name: 'Africa & Middle East', products: 6400, top: 'Tanzania, UAE, Morocco', growth: '+22%' },
    { name: 'Oceania', products: 3200, top: 'Australia, NZ, Fiji', growth: '+7%' },
    { name: 'Polar & Remote', products: 840, top: 'Antarctica, Iceland, Svalbard', growth: '+31%' },
  ];

  return (
    <div className="space-y-6">
      {/* Category Grid */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Product Categories</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CATEGORY_DATA.map(cat => (
            <div key={cat.id} className={`card p-4 border border-border/50 bg-gradient-to-br ${cat.color} hover:border-primary/30 transition-colors cursor-pointer`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                  <Icon name={cat.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-primary" />
                </div>
                <span className="text-2xs text-muted-foreground">{cat.suppliers} suppliers</span>
              </div>
              <p className="font-semibold text-foreground">{cat.name}</p>
              <div className="flex items-center justify-between mt-2">
                <div>
                  <p className="text-lg font-bold text-foreground">{fmt(cat.product_count)}</p>
                  <p className="text-2xs text-muted-foreground">{fmt(cat.active)} active</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-primary">{fmtCurrency(cat.avg_price)}</p>
                  <p className="text-2xs text-muted-foreground">avg price</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Destination Coverage */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Destination Coverage</h3>
        <div className="card border border-border/50 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-white/2">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Region</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Products</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Top Destinations</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Growth</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {destinations.map(d => (
                <tr key={d.name} className="hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground">{d.name}</td>
                  <td className="px-4 py-3 text-foreground font-semibold">{fmt(d.products)}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{d.top}</td>
                  <td className="px-4 py-3 text-emerald-400 text-sm font-medium">{d.growth}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: '85%' }} />
                      </div>
                      <span className="text-xs text-muted-foreground">85%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Multi-language & Multi-currency */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-4 border border-border/50">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="LanguageIcon" size={16} className="text-primary" />
            Multi-Language Support
          </h4>
          <div className="space-y-2">
            {[['English', '100%', 'bg-emerald-500'], ['French', '94%', 'bg-blue-500'], ['Spanish', '91%', 'bg-amber-500'], ['German', '88%', 'bg-purple-500'], ['Japanese', '76%', 'bg-rose-500'], ['Arabic', '68%', 'bg-teal-500'], ['Chinese', '62%', 'bg-orange-500'], ['Portuguese', '58%', 'bg-cyan-500']].map(([lang, pct, color]) => (
              <div key={lang} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-20">{lang}</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full ${color} rounded-full`} style={{ width: pct }} />
                </div>
                <span className="text-xs text-foreground w-8 text-right">{pct}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-4 border border-border/50">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="CurrencyDollarIcon" size={16} className="text-primary" />
            Multi-Currency Pricing
          </h4>
          <div className="space-y-2">
            {[['USD', '100%', 'bg-emerald-500'], ['EUR', '98%', 'bg-blue-500'], ['GBP', '96%', 'bg-amber-500'], ['AUD', '94%', 'bg-purple-500'], ['CAD', '92%', 'bg-rose-500'], ['SGD', '88%', 'bg-teal-500'], ['AED', '84%', 'bg-orange-500'], ['INR', '78%', 'bg-cyan-500']].map(([curr, pct, color]) => (
              <div key={curr} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-20">{curr}</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full ${color} rounded-full`} style={{ width: pct }} />
                </div>
                <span className="text-xs text-foreground w-8 text-right">{pct}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Supplier Mapping ────────────────────────────────────────────────────
function SuppliersTab() {
  return (
    <div className="space-y-4">
      <div className="card border border-border/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/30 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Supplier Product Mapping</h3>
          <div className="flex gap-2">
            <span className="px-2 py-1 rounded text-2xs bg-emerald-500/20 text-emerald-300">9 Suppliers Connected</span>
            <button className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1">
              <Icon name="ArrowPathIcon" size={12} />
              Sync All
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-white/2">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Supplier</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Products Mapped</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sync Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Sync</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Avg Quality</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Coverage</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {SUPPLIER_DATA.map(s => (
                <tr key={s.id} className="hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground">{s.name}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{s.type}</td>
                  <td className="px-4 py-3 text-foreground font-semibold">{fmt(s.products_mapped)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold ${STATUS_COLORS[s.sync_status] ?? ''}`}>
                      {s.sync_status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{s.last_sync}</td>
                  <td className="px-4 py-3 w-28"><QualityBar score={s.quality_avg} /></td>
                  <td className="px-4 py-3 text-emerald-400 text-sm font-medium">{s.coverage}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button className="p-1.5 rounded hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors" title="Sync Now">
                        <Icon name="ArrowPathIcon" size={14} />
                      </button>
                      <button className="p-1.5 rounded hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors" title="Configure">
                        <Icon name="CogIcon" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Normalization & Deduplication */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-4 border border-border/50">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="AdjustmentsHorizontalIcon" size={16} className="text-primary" />
            Product Normalization
          </h4>
          <div className="space-y-3">
            {[
              { label: 'Field Mapping Rules', value: '284 active', status: 'live' },
              { label: 'Schema Transformations', value: '47 templates', status: 'live' },
              { label: 'Price Normalization', value: '8 currencies', status: 'live' },
              { label: 'Category Mapping', value: '9 → unified', status: 'live' },
              { label: 'Attribute Standardization', value: '120+ fields', status: 'live' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                <span className="text-sm text-muted-foreground">{item.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{item.value}</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-4 border border-border/50">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="DocumentDuplicateIcon" size={16} className="text-primary" />
            Deduplication Engine
          </h4>
          <div className="space-y-3">
            {[
              { label: 'Duplicates Detected', value: '142', color: 'text-amber-400' },
              { label: 'Auto-Merged', value: '98', color: 'text-emerald-400' },
              { label: 'Pending Review', value: '44', color: 'text-amber-400' },
              { label: 'Similarity Threshold', value: '87%', color: 'text-foreground' },
              { label: 'Last Dedup Run', value: '2 hrs ago', color: 'text-muted-foreground' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                <span className="text-sm text-muted-foreground">{item.label}</span>
                <span className={`text-sm font-semibold ${item.color}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Approval Workflow ───────────────────────────────────────────────────
function ApprovalTab() {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      {/* Workflow Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Pending Review', value: '44', icon: 'ClockIcon', color: 'text-amber-400' },
          { label: 'Approved Today', value: '128', icon: 'CheckCircleIcon', color: 'text-emerald-400' },
          { label: 'Rejected Today', value: '12', icon: 'XCircleIcon', color: 'text-red-400' },
          { label: 'Auto-Approved', value: '84%', icon: 'BoltIcon', color: 'text-primary' },
        ].map(stat => (
          <div key={stat.label} className="card p-4 border border-border/50 text-center">
            <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={22} className={`${stat.color} mx-auto mb-2`} />
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Approval Queue */}
      <div className="card border border-border/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/30">
          <h3 className="text-sm font-semibold text-foreground">Approval Queue</h3>
        </div>
        <div className="divide-y divide-border/30">
          {APPROVAL_QUEUE.map(item => (
            <div
              key={item.id}
              className={`p-4 hover:bg-white/3 transition-colors cursor-pointer ${selected === item.id ? 'bg-white/5' : ''}`}
              onClick={() => setSelected(selected === item.id ? null : item.id)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-foreground text-sm truncate">{item.product}</p>
                    <QualityBar score={item.quality} />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{item.supplier}</span>
                    <span>·</span>
                    <span>{item.category}</span>
                    <span>·</span>
                    <span>Submitted {item.submitted}</span>
                    <span>·</span>
                    <span>Reviewer: {item.reviewer}</span>
                  </div>
                  {selected === item.id && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-medium text-amber-400">Issues to resolve:</p>
                      {item.issues.map((issue, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon name="ExclamationTriangleIcon" size={12} className="text-amber-400 flex-shrink-0" />
                          {issue}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button className="px-3 py-1.5 rounded text-xs bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition-colors font-medium">
                    Approve
                  </button>
                  <button className="px-3 py-1.5 rounded text-xs bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors font-medium">
                    Reject
                  </button>
                  <button className="px-3 py-1.5 rounded text-xs bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors font-medium">
                    AI Fix
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Versioning & Audit */}
      <div className="card p-4 border border-border/50">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="ClockIcon" size={16} className="text-primary" />
          Product Versioning & Audit History
        </h4>
        <div className="space-y-2">
          {[
            { action: 'AI Enrichment Applied', product: 'Machu Picchu Classic Trek', user: 'AI Engine', time: '2 min ago', type: 'ai' },
            { action: 'Price Updated', product: 'Santorini Sunset Sailing', user: 'Viator Sync', time: '8 min ago', type: 'sync' },
            { action: 'Approved', product: 'Serengeti Safari — 7 Nights', user: 'admin@worldwayluxe.com', time: '15 min ago', type: 'approval' },
            { action: 'Images Added (12)', product: 'Crystal Symphony Voyage', user: 'content@worldwayluxe.com', time: '1 hr ago', type: 'media' },
            { action: 'SEO Metadata Generated', product: 'Tokyo Cultural Immersion', user: 'AI Engine', time: '2 hrs ago', type: 'ai' },
          ].map((entry, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-border/20 last:border-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                entry.type === 'ai' ? 'bg-purple-500/20' : entry.type === 'sync' ? 'bg-blue-500/20' : entry.type === 'approval' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
              }`}>
                <Icon name={entry.type === 'ai' ? 'SparklesIcon' : entry.type === 'sync' ? 'ArrowPathIcon' : entry.type === 'approval' ? 'CheckCircleIcon' : 'PhotoIcon'} size={12} className={
                  entry.type === 'ai' ? 'text-purple-400' : entry.type === 'sync' ? 'text-blue-400' : entry.type === 'approval' ? 'text-emerald-400' : 'text-amber-400'
                } />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground"><span className="font-medium">{entry.action}</span> — {entry.product}</p>
                <p className="text-2xs text-muted-foreground">{entry.user} · {entry.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Collections & AI ────────────────────────────────────────────────────
function CollectionsTab() {
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const handleAIEnrich = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    setAiResponse('');
    try {
      const res = await fetch('/api/product-management', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'ai_enrich', prompt: aiPrompt }),
      });
      const data = await res.json();
      setAiResponse(data.result || 'AI enrichment complete.');
    } catch {
      setAiResponse('AI enrichment service ready. Connect your AI provider to generate product content.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Dynamic Collections */}
      <div className="card border border-border/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/30 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Dynamic Collections & Merchandising</h3>
          <button className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1">
            <Icon name="PlusIcon" size={12} />
            New Collection
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-white/2">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Collection</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Products</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Views</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Conversion</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">AI Curated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {COLLECTIONS.map(c => (
                <tr key={c.id} className="hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                  <td className="px-4 py-3 text-foreground">{fmt(c.products)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmt(c.views)}</td>
                  <td className="px-4 py-3 text-emerald-400 font-medium">{c.conversions}%</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold ${STATUS_COLORS[c.status] ?? ''}`}>
                      {c.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {c.ai_curated ? (
                      <span className="px-2 py-0.5 rounded text-2xs bg-purple-500/20 text-purple-300">✦ AI Curated</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Manual</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Product Enrichment */}
      <div className="card p-4 border border-border/50">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="SparklesIcon" size={16} className="text-primary" />
          AI Product Enrichment & SEO Content Generation
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {[
            { label: 'SEO Titles Generated', value: '48,240', icon: 'DocumentTextIcon' },
            { label: 'Meta Descriptions', value: '48,240', icon: 'TagIcon' },
            { label: 'AI Descriptions', value: '31,840', icon: 'PencilSquareIcon' },
            { label: 'Cross-sell Suggestions', value: '124,800', icon: 'ArrowsRightLeftIcon' },
          ].map(stat => (
            <div key={stat.label} className="flex items-center gap-3 p-3 rounded-lg bg-white/3 border border-border/30">
              <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-lg font-bold text-foreground">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <textarea
            value={aiPrompt}
            onChange={e => setAiPrompt(e.target.value)}
            placeholder="Enter product name or ID to generate AI-enriched content, SEO metadata, or cross-sell recommendations..."
            className="input-field w-full text-sm resize-none"
            rows={3}
          />
          <div className="flex gap-2">
            <button
              onClick={handleAIEnrich}
              disabled={aiLoading || !aiPrompt.trim()}
              className="btn-primary text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-50"
            >
              {aiLoading ? <Icon name="ArrowPathIcon" size={14} className="animate-spin" /> : <Icon name="SparklesIcon" size={14} />}
              {aiLoading ? 'Enriching...' : 'AI Enrich Product'}
            </button>
            <button className="btn-secondary text-sm px-4 py-2 flex items-center gap-2">
              <Icon name="BoltIcon" size={14} />
              Bulk AI Enrich
            </button>
          </div>
          {aiResponse && (
            <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 text-sm text-foreground">
              {aiResponse}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Performance Analytics ───────────────────────────────────────────────
function AnalyticsTab() {
  const topProducts = [
    { name: 'Machu Picchu Classic Trek', views: 48200, bookings: 1840, revenue: 5320000, conversion: 3.8 },
    { name: 'Santorini Sunset Sailing', views: 62400, bookings: 4120, revenue: 597400, conversion: 6.6 },
    { name: 'Crystal Symphony Voyage', views: 18900, bookings: 420, revenue: 5208000, conversion: 2.2 },
    { name: 'Serengeti Safari', views: 34100, bookings: 980, revenue: 5684000, conversion: 2.9 },
    { name: 'Tokyo Cultural Immersion', views: 28700, bookings: 1240, revenue: 3968000, conversion: 4.3 },
  ];

  return (
    <div className="space-y-4">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Product Views', value: '2.4M', sub: '+18% vs last month', icon: 'EyeIcon', color: 'from-blue-600/20 to-transparent' },
          { label: 'Booking Conversions', value: '4.2%', sub: '+0.3% improvement', icon: 'ShoppingCartIcon', color: 'from-emerald-600/20 to-transparent' },
          { label: 'Revenue Attributed', value: '$48.2M', sub: 'from product catalog', icon: 'BanknotesIcon', color: 'from-amber-600/20 to-transparent' },
          { label: 'Avg Quality Score', value: '91.4', sub: '+2.1 this quarter', icon: 'StarIcon', color: 'from-purple-600/20 to-transparent' },
        ].map(kpi => (
          <KPICard key={kpi.label} {...kpi} />
        ))}
      </div>

      {/* Top Performing Products */}
      <div className="card border border-border/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/30">
          <h3 className="text-sm font-semibold text-foreground">Top Performing Products</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-white/2">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Product</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Views</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bookings</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Revenue</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Conversion</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {topProducts.map((p, i) => (
                <tr key={p.name} className="hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-muted-foreground w-4">#{i + 1}</span>
                      <span className="font-medium text-foreground text-sm">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmt(p.views)}</td>
                  <td className="px-4 py-3 text-foreground font-medium">{fmt(p.bookings)}</td>
                  <td className="px-4 py-3 text-emerald-400 font-semibold">{fmtCurrency(p.revenue)}</td>
                  <td className="px-4 py-3 text-primary font-medium">{p.conversion}%</td>
                  <td className="px-4 py-3 w-28">
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(p.conversion * 12, 100)}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Integration Map */}
      <div className="card p-4 border border-border/50">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="CpuChipIcon" size={16} className="text-primary" />
          Platform Integration Status
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {[
            'AI Concierge', 'Booking Engine', 'Dynamic Packages', 'Tour Platform',
            'Flights', 'Hotels', 'CRM', 'Analytics',
            'Enterprise Admin', 'Super Admin', 'White-Label', 'Supplier Registry',
          ].map(integration => (
            <div key={integration} className="flex items-center gap-2 p-2 rounded-lg bg-white/3 border border-border/30">
              <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
              <span className="text-xs text-foreground">{integration}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProductManagementContent() {
  const [activeTab, setActiveTab] = useState('catalog');
  const [overview, setOverview] = useState<ProductOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/product-management?action=overview');
      const data = await res.json();
      setOverview(data.overview ?? null);
    } catch {
      // Use fallback data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const ov = overview ?? {
    total_products: 53960,
    active_products: 51705,
    pending_approval: 44,
    draft_products: 211,
    suppliers_connected: 9,
    categories: 9,
    destinations: 184,
    avg_quality_score: 91.4,
    ai_enriched: 48240,
    last_sync: '2 min ago',
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <Icon name="CubeIcon" size={20} className="text-primary" />
                <h1 className="text-lg font-bold text-foreground">Travel Product Management Platform</h1>
                <span className="px-2 py-0.5 rounded-full text-2xs bg-emerald-500/20 text-emerald-300 font-semibold">LIVE</span>
              </div>
              <p className="text-xs text-muted-foreground">Centralized PIM · {fmt(ov.total_products)} products · {ov.suppliers_connected} suppliers · Last sync {ov.last_sync}</p>
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary text-xs px-3 py-2 flex items-center gap-1.5">
                <Icon name="ArrowPathIcon" size={13} />
                Sync All
              </button>
              <button className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5">
                <Icon name="PlusIcon" size={13} />
                Add Product
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* KPI Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPICard label="Total Products" value={fmt(ov.total_products)} sub={`${fmt(ov.active_products)} active`} icon="CubeIcon" color="from-blue-600/20 to-transparent" />
          <KPICard label="Pending Approval" value={fmt(ov.pending_approval)} sub="Requires review" icon="ClockIcon" color="from-amber-600/20 to-transparent" />
          <KPICard label="Suppliers Connected" value={String(ov.suppliers_connected)} sub={`${ov.categories} categories`} icon="BuildingStorefrontIcon" color="from-emerald-600/20 to-transparent" />
          <KPICard label="AI Enriched" value={fmt(ov.ai_enriched)} sub="SEO + content" icon="SparklesIcon" color="from-purple-600/20 to-transparent" />
          <KPICard label="Avg Quality Score" value={String(ov.avg_quality_score)} sub={`${ov.destinations} destinations`} icon="StarIcon" color="from-rose-600/20 to-transparent" />
        </div>

        {/* Tabs */}
        <div className="border-b border-border/50">
          <div className="flex gap-1 overflow-x-auto pb-0 scrollbar-hide">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={15} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'catalog' && <CatalogTab products={PRODUCT_SAMPLE} />}
        {activeTab === 'categories' && <CategoriesTab />}
        {activeTab === 'suppliers' && <SuppliersTab />}
        {activeTab === 'approval' && <ApprovalTab />}
        {activeTab === 'collections' && <CollectionsTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
      </div>
    </div>
  );
}
